import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Briefcase, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import {
  FEE_TYPES, INVOICE_STATUSES, PLACEMENT_STATUSES, computePlacementMoney,
  fmtMoney, candidateName, todayISO,
} from './constants';

const EMPTY = {
  jobId: '', candidateId: 'manual', candidateName: '', startDate: '', salary: '', currency: 'EUR',
  feeType: 'percentage', feePercentage: '', feeAmount: '', commissionRate: 10,
  guaranteeDays: 90, invoiceStatus: 'pending', status: 'active', notes: '',
};

export const PlacementModal = ({ open, onOpenChange, onSaved, placement, defaultJobId, defaultJobTitle, defaultCandidate }) => {
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const prefillRef = useRef(null);
  const isEdit = !!placement;
  const jobLocked = isEdit || !!defaultJobId;
  const candidateLocked = isEdit || !!defaultCandidate;

  useEffect(() => {
    if (!open) return;
    if (placement) {
      prefillRef.current = 'edit';
      setForm({
        jobId: placement.job_id || '', candidateId: placement.candidate_id || 'manual',
        candidateName: placement.candidate_name || '', startDate: placement.start_date || '',
        salary: placement.salary ?? '', currency: placement.currency || 'EUR',
        feeType: placement.fee_type || 'percentage', feePercentage: placement.fee_percentage ?? '',
        feeAmount: placement.fee_amount ?? '', commissionRate: placement.commission_rate ?? 10,
        guaranteeDays: placement.guarantee_days ?? 90, invoiceStatus: placement.invoice_status || 'pending',
        status: placement.status || 'active', notes: placement.notes || '',
      });
      return;
    }
    prefillRef.current = null;
    setForm({
      ...EMPTY,
      startDate: todayISO(),
      jobId: defaultJobId || '',
      candidateId: defaultCandidate?.id || 'manual',
      candidateName: defaultCandidate ? candidateName(defaultCandidate) : '',
    });
    crmFetch('/jobs').then((r) => setJobs((r.data || []).filter((j) => j.status !== 'lost')));
  }, [open, placement, defaultJobId, defaultCandidate]);

  // Prefill fee terms + salary from the selected job (create mode)
  useEffect(() => {
    if (!open || isEdit || !form.jobId || !jobs.length) return;
    if (prefillRef.current === form.jobId) return;
    const j = jobs.find((x) => x.id === form.jobId);
    if (!j) return;
    prefillRef.current = form.jobId;
    setForm((f) => ({
      ...f,
      currency: j.currency || 'EUR',
      feeType: j.fee_type || 'percentage',
      feePercentage: j.fee_percentage ?? '',
      feeAmount: j.fee_amount ?? '',
      salary: f.salary || (j.salary_max ?? j.salary_min ?? ''),
    }));
  }, [open, isEdit, form.jobId, jobs]);

  // Pipeline candidates for the selected job (create mode, candidate not locked)
  useEffect(() => {
    if (!open || isEdit || candidateLocked || !form.jobId) { setCandidates([]); return; }
    crmFetch(`/candidates?jobId=${form.jobId}`).then((r) => setCandidates(r.data || []));
  }, [open, isEdit, candidateLocked, form.jobId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickCandidate = (v) => {
    if (v === 'manual') { set('candidateId', 'manual'); return; }
    const c = candidates.find((x) => x.id === v);
    setForm((f) => ({ ...f, candidateId: v, candidateName: c ? candidateName(c) : f.candidateName }));
  };

  const money = computePlacementMoney(form);
  const selectedJob = jobs.find((j) => j.id === form.jobId);
  const lockedJobLabel = defaultJobTitle || placement?.job_title || selectedJob?.title || 'Selected job';

  const submit = async () => {
    if (!isEdit && !form.jobId) { toast.error('Please select a job'); return; }
    if (!form.candidateName.trim()) { toast.error('Candidate name is required'); return; }
    setSaving(true);
    try {
      const shared = {
        candidateName: form.candidateName, startDate: form.startDate || null,
        salary: form.salary, currency: form.currency, feeType: form.feeType,
        feePercentage: form.feePercentage, feeAmount: form.feeAmount,
        commissionRate: form.commissionRate, guaranteeDays: form.guaranteeDays,
        invoiceStatus: form.invoiceStatus, notes: form.notes,
      };
      const res = isEdit
        ? await crmFetch(`/placements/${placement.id}`, { method: 'PATCH', body: JSON.stringify({ ...shared, status: form.status }) })
        : await crmFetch('/placements', {
            method: 'POST',
            body: JSON.stringify({
              ...shared,
              jobId: form.jobId,
              candidateId: form.candidateId === 'manual' ? null : form.candidateId,
            }),
          });
      if (!res.success) { toast.error(res.message || 'Failed to save placement'); return; }
      toast.success(isEdit ? 'Placement updated' : 'Placement recorded 🎉');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="placement-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Placement' : 'Record Placement'}</DialogTitle>
          <DialogDescription>Confirm the placement terms — revenue and commission are calculated automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {jobLocked ? (
            <div>
              <Label className="mb-1.5 block">Job</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-gray-50 text-sm text-gray-700" data-testid="placement-job-locked">
                <Briefcase className="w-4 h-4 text-gray-400" />
                {lockedJobLabel}
              </div>
            </div>
          ) : (
            <div>
              <Label className="mb-1.5 block">Job *</Label>
              <Select value={form.jobId} onValueChange={(v) => { set('jobId', v); set('candidateId', 'manual'); }}>
                <SelectTrigger data-testid="placement-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}{j.accountName ? ` · ${j.accountName}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {candidateLocked ? (
            <div>
              <Label className="mb-1.5 block">Candidate</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-gray-50 text-sm text-gray-700" data-testid="placement-candidate-locked">
                <UserCheck className="w-4 h-4 text-gray-400" />
                {form.candidateName || 'Candidate'}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Pipeline Candidate</Label>
                <Select value={form.candidateId} onValueChange={pickCandidate} disabled={!form.jobId}>
                  <SelectTrigger data-testid="placement-candidate-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Type name manually</SelectItem>
                    {candidates.map((c) => <SelectItem key={c.id} value={c.id}>{candidateName(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Candidate Name *</Label>
                <Input value={form.candidateName} onChange={(e) => set('candidateName', e.target.value)} placeholder="e.g. Anna Braun" data-testid="placement-candidate-name-input" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Start Date</Label>
              <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} data-testid="placement-start-date-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Guarantee (days)</Label>
              <Input type="number" value={form.guaranteeDays} onChange={(e) => set('guaranteeDays', e.target.value)} data-testid="placement-guarantee-days-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Agreed Salary</Label>
              <Input type="number" value={form.salary} onChange={(e) => set('salary', e.target.value)} data-testid="placement-salary-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Currency</Label>
              <Input value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} maxLength={3} data-testid="placement-currency-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Fee Type</Label>
              <Select value={form.feeType} onValueChange={(v) => set('feeType', v)}>
                <SelectTrigger data-testid="placement-fee-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FEE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">{form.feeType === 'fixed' ? 'Fee Amount' : 'Fee %'}</Label>
              {form.feeType === 'fixed'
                ? <Input type="number" value={form.feeAmount} onChange={(e) => set('feeAmount', e.target.value)} data-testid="placement-fee-amount-input" />
                : <Input type="number" value={form.feePercentage} onChange={(e) => set('feePercentage', e.target.value)} data-testid="placement-fee-percentage-input" />}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Commission Rate %</Label>
              <Input type="number" value={form.commissionRate} onChange={(e) => set('commissionRate', e.target.value)} data-testid="placement-commission-rate-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Invoice Status</Label>
              <Select value={form.invoiceStatus} onValueChange={(v) => set('invoiceStatus', v)}>
                <SelectTrigger data-testid="placement-invoice-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INVOICE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isEdit && (
            <div>
              <Label className="mb-1.5 block">Placement Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="placement-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLACEMENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">"Fell Out" reverses this placement's revenue and commission.</p>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="placement-notes-input" />
          </div>

          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm" data-testid="placement-preview">
            <span className="text-emerald-800">Fee revenue: <strong>{fmtMoney(money.revenue, form.currency || 'EUR') || '—'}</strong></span>
            <span className="text-emerald-600 mx-2">·</span>
            <span className="text-emerald-800">Commission ({money.rate}%): <strong>{fmtMoney(money.commission, form.currency || 'EUR') || '—'}</strong></span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="placement-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="placement-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Record Placement')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
